import { useId } from "react";
import type { FC } from "react";

type Direction = "horizontal" | "vertical" | "both";

interface MetamorphopsiaToolProps {
	enabled: boolean;
	amplitude: number;
	frequency: number;
	direction: Direction;
	mode: "whole" | "region";
	bridge: number;
	hasRegion: boolean;
	onChange: (patch: {
		enabled?: boolean;
		amplitude?: number;
		frequency?: number;
		direction?: Direction;
		mode?: "whole" | "region";
		bridge?: number;
	}) => void;
	onClearRegion: () => void;
}

const DIRECTION_OPTIONS: Direction[] = ["horizontal", "vertical", "both"];

export const MetamorphopsiaTool: FC<MetamorphopsiaToolProps> = ({
	enabled,
	amplitude,
	frequency,
	direction,
	mode,
	bridge,
	hasRegion,
	onChange,
	onClearRegion,
}) => {
	const amplitudeId = useId();
	const frequencyId = useId();
	const bridgeId = useId();

	return (
		<div className="p-4">
			<div className="mb-2 font-semibold">Metamorphopsia</div>
			<p className="text-xs text-gray-600 mb-4">
				Wave-like distortion where straight lines appear wavy.
			</p>

			{enabled && (
				<div className="space-y-4">
					{/* Whole image | Region */}
					<div className="flex gap-2">
						<button
							type="button"
							className={`flex-1 min-h-[44px] rounded font-medium ${mode !== "region" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
							onClick={() => onChange({ mode: "whole" })}
						>
							Whole image
						</button>
						<button
							type="button"
							className={`flex-1 min-h-[44px] rounded font-medium ${mode === "region" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
							onClick={() => onChange({ mode: "region" })}
						>
							Region
						</button>
					</div>

					{mode === "region" && (
						<p className="text-sm text-gray-600">
							Tap the face to place the wave region, then drag to move, drag the
							corner to resize.
						</p>
					)}

					<div>
						<label className="block text-sm mb-1" htmlFor={amplitudeId}>
							Amplitude: {amplitude.toFixed(0)}
						</label>
						<input
							type="range"
							min={0}
							max={20}
							step={0.1}
							id={amplitudeId}
							value={amplitude}
							onChange={(e) => onChange({ amplitude: Number(e.target.value) })}
							className="w-full"
						/>
					</div>

					<div>
						<label className="block text-sm mb-1" htmlFor={frequencyId}>
							Frequency: {frequency.toFixed(0)}
						</label>
						<input
							type="range"
							min={0.1}
							max={10}
							step={0.1}
							id={frequencyId}
							value={frequency}
							onChange={(e) => onChange({ frequency: Number(e.target.value) })}
							className="w-full"
						/>
					</div>

					{mode === "region" && (
						<div>
							<label className="block text-sm mb-1" htmlFor={bridgeId}>
								Bridge (edge softness): {Math.round(bridge * 100)}%
							</label>
							<input
								type="range"
								min={0}
								max={1}
								step={0.01}
								id={bridgeId}
								value={bridge}
								disabled={!hasRegion}
								onChange={(e) => onChange({ bridge: Number(e.target.value) })}
								className="w-full"
							/>
						</div>
					)}

					<div>
						<p className="block text-sm mb-1">Direction</p>
						<div className="flex gap-1">
							{DIRECTION_OPTIONS.map((dir) => (
								<button
									key={dir}
									type="button"
									className={`min-h-[44px] rounded text-sm flex-1 ${
										direction === dir
											? "bg-blue-500 text-white"
											: "bg-gray-200 text-gray-700"
									}`}
									onClick={() => onChange({ direction: dir })}
								>
									{dir.charAt(0).toUpperCase() + dir.slice(1)}
								</button>
							))}
						</div>
					</div>

					{mode === "region" && hasRegion && (
						<button
							type="button"
							className="min-h-[44px] px-3 rounded bg-gray-200 w-full"
							onClick={onClearRegion}
						>
							Clear region
						</button>
					)}
				</div>
			)}
		</div>
	);
};
